import { AxiosResponse } from 'axios';
import { BaseService, ChoiceField, PaginatedResponse, StorableBaseService } from './_base';
import axios from "../axios";
import branding from '../branding';
import querystring from 'query-string';

import { putEntry, getEntries, getEntry } from '../globals/db';
import { AxiosResponseOk } from '../globals/http-responses';
import { getRemoteOrLocal } from '../globals/cache';
import { session } from '../globals/session';
import institutionService from './institutions';
import { registry } from '../store/_base';


interface RequestPasswordResetBody {
    username: string;
}

interface SetPasswordBody {
    current_password: string;
    new_password: string;
    new_password_confirm: string;
}


interface SetPasswordViaCodeBody {
    action: string;
    new_password: string;
}


interface Account {
    id: string;
    created_at: string;
    modified_at: string;
    institution_id: string;
    employee_id?: string;
    is_web_recording: boolean;
    full_name?: string;
    email: string;
    role: string;
    permissions: Array<string>;
}

interface OAuthResponse {
    access_token: string;
    expires_in: number;
    token_type: string;
    scope: string;
    refresh_token: string;
}

interface TimeSecLoginResponse {
    token: string;
    expires: string;
}

interface LoginCodeAuthenticationBody {
    code: string;
}

interface LoginCodeAuthenticationResponse {
    token: string;
    expires_at: string;
}

interface SendLoginCodeBody {
    email: string;
}

interface LoginSuggestion {
    name: string;
    role: string;
    institution: string;
    expires_at: string;
    code: string;
}


class AccountService extends StorableBaseService<Account> {
    constructor() {
        super("/api/v2/accounts/users/");
        this.cacheTimeout = 60 * 15;
    }

    validateToken(): Promise<boolean> {
        // Check if the token is still valid by sending a request to the
        // accounts endpoint and verify that a user account is returned.
        return this
            .list()
            .then((response: AxiosResponse<PaginatedResponse<Account>>) => {
                if (response.data.results.length > 0) {
                    return Promise.resolve(true);
                }
                return Promise.resolve(false);
            })
            .catch(() => {
                return Promise.resolve(false);
            });
    }

    listRoles(page=1, pagesize=50, query='', ids: Array<string>=[]): Promise<AxiosResponse<PaginatedResponse<ChoiceField>>> {
        return getRemoteOrLocal<PaginatedResponse<ChoiceField>>(
            'store/employees/employees/roles',
            this._getChoices.bind(this), 'roles/', page, pagesize, query, ids
        );
    }

    requestPasswordChange(resourceId: string): Promise<undefined> {
        return this._post(resourceId + '/change-password/', {}) as unknown as Promise<undefined>;
    }

    requestPasswordReset(body: RequestPasswordResetBody): Promise<undefined> {
        return this._post('reset-password/', body) as unknown as Promise<undefined>;
    }

    setPassword(resourceId: string, body: SetPasswordBody): Promise<undefined> {
        return this._post(resourceId + '/set-password/', body) as unknown as Promise<undefined>;
    }

    setPasswordViaCode(body: SetPasswordViaCodeBody): Promise<undefined> {
        return this._post('set-password/', body) as unknown as Promise<undefined>;
    }

    loginCodeAuthentication(body: LoginCodeAuthenticationBody): Promise<AxiosResponse<LoginCodeAuthenticationResponse>> {
        return this._post('login-code-authentication/', body) as unknown as Promise<AxiosResponse<LoginCodeAuthenticationResponse>>;
    }

    sendLoginCode(body: SendLoginCodeBody): Promise<AxiosResponse<undefined>> {
        return this._post('send-login-code/', body) as unknown as Promise<AxiosResponse<undefined>>;
    }

    async login(username: string, password: string): Promise<AxiosResponse<OAuthResponse>> {
        const payload = {
            "grant_type": "password",
            "username": username,
            "password": password,
            "client_id": import.meta.env.VITE_APP_OAUTH_CLIENT_ID,
            "client_secret": import.meta.env.VITE_APP_OAUTH_CLIENT_SECRET,
        };

        const response = await axios.post<OAuthResponse>("/o/token/", querystring.stringify(payload));
        localStorage.accessToken = response.data.access_token;

        const [accounts, institutions] = await Promise.all([
            this.list(),
            institutionService.list(),
        ]);
        session.setAccount(accounts.data.results[0]);
        session.setInstitution(institutions.data.results[0]);

        return response;
    }

    async loginV2(username: string, password: string): Promise<AxiosResponse<Array<LoginSuggestion>>> {
        return await axios.post<Array<LoginSuggestion>>("api/v2/accounts/users/login/", {
            username,
            password,
            is_mobile: branding.isMobile,
        });
    }

    async loginTimesec(systemId: string): Promise<AxiosResponse<TimeSecLoginResponse>> {
        const minValidity = new Date();
        minValidity.setDate(minValidity.getDate() - 1);

        // If token is already cached, do not retrieve a new one
        const token = await getEntry<TimeSecLoginResponse>('timesec/access-tokens', systemId);
        if (token && token.expires > minValidity.toISOString()) return new AxiosResponseOk(token);

        // If card is unknown, request a token
        const response = await this._post("timesec-login/", {
            "system_id": systemId,
        }) as unknown as AxiosResponse<TimeSecLoginResponse>;

        await putEntry('timesec/access-tokens', { id: systemId, token: response.data.token, expires: response.data.expires });

        return response;
    }

    deleteUserCache() {
        for (const name of Object.keys(localStorage)) if (name.startsWith('store__')) localStorage.removeItem(name);
    }

    logout() {
        localStorage.removeItem('accessToken');
        session.reset();

        localStorage.removeItem('accessMode');
        localStorage.removeItem('deviceConfig');
        localStorage.removeItem('autoLogout');
        localStorage.removeItem('licensePrompt');
        localStorage.removeItem('institution');

        // Don't delete the cache for blue brands
        if (branding.id != 'blue') this.deleteUserCache();

        // Clear in-memory store caches to prevent stale data after re-login
        for (const store of registry) store.clearMemory();

    }
}

const accountService = new AccountService();

export { AccountService, accountService, Account, SetPasswordBody, LoginSuggestion }

export default accountService;
